import { useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import { Add, BoltOutlined, Delete } from "@mui/icons-material";
import { SecaoConfiguracao } from "../../../../core/components/SecaoConfiguracao";
import { IMarketplace } from "../../../interfaces/IMarketplace";
import { IIntegracaoWhatsApp } from "../../../interfaces/IIntegracaoMarketplace";
import { IMensagemRapidaWhatsApp } from "../../../interfaces/IMensagemRapidaWhatsApp";
import { useMarketplaces } from "../../../hooks/useMarketplaces";
import { useSnackbar } from "../../../../core/contexts/SnackbarContext";

interface ConfigurarMensagensRapidasWhatsAppProps {
    marketplace: IMarketplace;
    refresh: () => void;
}

export const ConfigurarMensagensRapidasWhatsApp = ({ marketplace, refresh }: ConfigurarMensagensRapidasWhatsAppProps) => {
    const [open, setOpen] = useState(false);
    const [novaMensagem, setNovaMensagem] = useState<IMensagemRapidaWhatsApp>({ chave: "", mensagem: "" });
    const { salvar, loading } = useMarketplaces();
    const notificar = useSnackbar();

    const integracao = marketplace?.configuracao?.integracao as IIntegracaoWhatsApp;
    const mensagensRapidas = integracao?.mensagensRapidas ?? [];

    const handleOpen = () => {
        setNovaMensagem({ chave: "", mensagem: "" });
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleExcluir = async (indexParaRemover: number) => {
        if (loading) return;

        const mensagensRestantes = mensagensRapidas.filter((_, index) => index !== indexParaRemover);

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                integracao: {
                    ...integracao,
                    mensagensRapidas: mensagensRestantes
                }
            }
        });
        refresh();
    };

    const handleCadastrar = async () => {
        if (loading) return;

        if (!novaMensagem.chave.trim()) {
            return notificar("Informe a chave da mensagem rápida", { severity: "error" });
        }

        if (!novaMensagem.mensagem.trim()) {
            return notificar("Informe o texto da mensagem rápida", { severity: "error" });
        }

        const chaveJaExiste = mensagensRapidas.some(
            (m) => m.chave.toLowerCase() === novaMensagem.chave.trim().toLowerCase()
        );

        if (chaveJaExiste) {
            return notificar("Já existe uma mensagem rápida com essa chave", { severity: "error" });
        }

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                integracao: {
                    ...integracao,
                    mensagensRapidas: [...mensagensRapidas, { chave: novaMensagem.chave.trim(), mensagem: novaMensagem.mensagem.trim() }]
                }
            }
        });
        handleClose();
        refresh();
    };

    return (
        <SecaoConfiguracao
            titulo="Mensagens rápidas"
            descricao="Atalhos que o atendente dispara digitando /chave"
            icone={<BoltOutlined />}
            acoes={
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleOpen}
                >
                    Criar
                </Button>
            }
        >
            {mensagensRapidas.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {mensagensRapidas.map((mensagem, index) => (
                        <Box
                            key={index}
                            sx={{
                                p: 1.5,
                                borderRadius: "12px",
                                border: theme => `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                                <Chip
                                    size="small"
                                    label={`/${mensagem.chave}`}
                                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                                />
                                {loading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <IconButton color="error" size="small" onClick={() => handleExcluir(index)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                                {mensagem.mensagem}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box sx={{
                    flex: 1,
                    minHeight: "120px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    alignItems: "center",
                    justifyContent: "center",
                    border: theme => `1px dashed ${theme.palette.divider}`,
                    borderRadius: "12px",
                    p: 2
                }}>
                    <Typography variant="body2" fontWeight={500}>Nenhuma mensagem rápida</Typography>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                        Cadastre respostas prontas para agilizar o atendimento manual
                    </Typography>
                </Box>
            )}

            <Dialog open={open} onClose={handleClose} fullWidth>
                <DialogTitle>Nova Mensagem Rápida</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Chave"
                        fullWidth
                        sx={{ mt: "1.5rem" }}
                        value={novaMensagem.chave}
                        onChange={(e) => setNovaMensagem((prev) => ({ ...prev, chave: e.target.value }))}
                        helperText='O usuário digita "/" seguido desta chave para acionar a mensagem'
                        InputProps={{
                            startAdornment: <Typography sx={{ mr: 0.5, color: "text.secondary" }}>/</Typography>
                        }}
                    />
                    <TextField
                        label="Mensagem"
                        fullWidth
                        multiline
                        minRows={3}
                        sx={{ mt: "1.5rem" }}
                        value={novaMensagem.mensagem}
                        onChange={(e) => setNovaMensagem((prev) => ({ ...prev, mensagem: e.target.value }))}
                        helperText="Texto que será enviado ao acionar a mensagem rápida"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">Cancelar</Button>
                    <Button
                        onClick={handleCadastrar}
                        variant={loading ? "outlined" : "contained"}
                    >
                        {loading ? <CircularProgress size={20} /> : "Cadastrar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </SecaoConfiguracao>
    );
};
